import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Image,
  useToast,
} from "@chakra-ui/react";
import { Link, useHistory } from "react-router-dom";
import OrgLogo from "assets/img/ORG.png";
import FactsLogo from "assets/img/Facts.jpg";
import SconexLogo from "assets/img/sconex.png";
import { forgotPassword, verifyOtp, resetPassword } from "services/authService";
import { getVideoSource } from "services/videoService";
import { LOGIN_BACKGROUND_VIDEO } from "constants/videoAssets";
import "../../styles/auth/SignIn.css";

const STEP_TITLES = {
  1: "Reset Your Password",
  2: "Enter Verification Code",
  3: "Set New Password",
};

const STEP_SUBTITLES = {
  1: "Enter your registered email and we'll send you a verification code.",
  2: "We've sent a verification code to your email. Enter it below to continue.",
  3: "Choose a new password to secure your account.",
};

function ForgotPassword() {
  const toast = useToast();
  const history = useHistory();
  const primaryTextColor = "#f8fafc";
  const secondaryTextColor = "rgba(226, 232, 240, 0.85)";
  const focusColor = "#7dd3fc";

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});

  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadBackground() {
      try {
        const url = await getVideoSource(LOGIN_BACKGROUND_VIDEO);
        if (isMounted) {
          setBackgroundUrl(url);
          setIsVideoReady(true);
        }
      } catch (error) {
        console.error("[FORGOT PASSWORD] Failed to load background video", error);
        setIsVideoReady(false);
      }
    }

    loadBackground();

    return () => {
      isMounted = false;
    };
  }, []);

  const showError = (error, fallback) => {
    toast({
      title: "Error",
      description: error.response?.data?.message || fallback,
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  };

  const showSuccess = (title, description) => {
    toast({
      title,
      description,
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  };

  const validateEmailStep = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Email is invalid";
    return newErrors;
  };

  const validateOtpStep = () => {
    const newErrors = {};
    if (!otp) newErrors.otp = "OTP is required";
    return newErrors;
  };

  const validatePasswordStep = () => {
    const newErrors = {};
    if (!newPassword) newErrors.newPassword = "New password is required";
    else if (newPassword.length < 6)
      newErrors.newPassword = "Password must be at least 6 characters";
    if (newPassword !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    return newErrors;
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    const validationErrors = validateEmailStep();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await forgotPassword(email);
      showSuccess(
        "OTP sent",
        response.data?.message || `A verification code has been sent to ${email}.`
      );
      setErrors({});
      setStep(2);
    } catch (error) {
      showError(error, "Failed to send OTP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const validationErrors = validateOtpStep();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await verifyOtp({ email, otp });
      setResetToken(response.data?.resetToken || "");
      showSuccess("OTP verified", response.data?.message || "Your code has been verified.");
      setErrors({});
      setStep(3);
    } catch (error) {
      showError(error, "Failed to verify OTP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const validationErrors = validatePasswordStep();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await resetPassword({ resetToken, newPassword });
      showSuccess(
        "Password reset",
        response.data?.message || "Your password has been reset successfully. Please sign in."
      );
      history.push("/auth/signin");
    } catch (error) {
      showError(error, "Failed to reset password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex className="signin-shell">
      {backgroundUrl && (
        <video
          className={`signin-bg-video ${isVideoReady ? "is-visible" : ""}`}
          src={backgroundUrl}
          autoPlay
          muted
          loop
          playsInline
        />
      )}
      <Box className="signin-hero">
        <Box className="hero-content">
          <Flex justify="center" align="center" mb="60px">
            <Image
              src={SconexLogo}
              alt="Sconex Logo"
              className="hero-logo"
              draggable="false"
            />
          </Flex>
          <Box position="relative" height="120px">
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite"
            >
              Welcome to ORGI Dashboard
            </Text>
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite 4s"
            >
              Explore Soft Drinks Data
            </Text>
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite 8s"
            >
              Image to Insight – Instantly.
            </Text>
          </Box>
          <Text fontSize="lg" color="rgba(226, 232, 240, 0.92)" mt={8} lineHeight="tall">
            Discover detailed reports, vibrant visuals, and smart analysis all in
            one intelligent dashboard.
          </Text>
        </Box>
      </Box>

      <Box className="signin-panel" color={primaryTextColor}>
        <Flex className="signin-content" align="center" justify="center">
          <Box className="signin-card">
            <Box className="card-shimmer" />
            <Stack align="center" spacing={3} mb={6}>
              <Flex gap={4} align="center" justify="center">
                <Image
                  src={OrgLogo}
                  alt="ORG Logo"
                  className="signin-logo"
                  draggable="false"
                  background="#fff"
                  borderRadius="5px"
                  padding="5px"
                />
                <Image
                  src={FactsLogo}
                  alt="FACTS Logo"
                  width="90px"
                  height="40px"
                  className="signin-logo"
                  draggable="false"
                />
              </Flex>
              <Text fontSize="2xl" fontWeight="bold" color={primaryTextColor}>
                {STEP_TITLES[step]}
              </Text>
              <Text
                fontSize="md"
                textAlign="center"
                color={secondaryTextColor}
                px={4}
              >
                {STEP_SUBTITLES[step]}
              </Text>
            </Stack>

            {step === 1 && (
              <form onSubmit={handleRequestOtp}>
                <FormControl mb={5}>
                  <FormLabel color={secondaryTextColor} fontWeight="600">
                    Email
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="signin-input"
                    _focus={{
                      borderColor: focusColor,
                      boxShadow: `0 0 0 1px ${focusColor}`,
                    }}
                  />
                  {errors.email && (
                    <Text color="red.300" fontSize="sm" mt={2}>
                      {errors.email}
                    </Text>
                  )}
                </FormControl>

                <Button
                  type="submit"
                  width="100%"
                  className="signin-button"
                  py={6}
                  isLoading={submitting}
                  bgGradient="linear(140deg, #111111 0%, #000000 55%, #1a1a1a 100%)"
                  color="white"
                  _hover={{
                    bgGradient:
                      "linear(140deg, #050505 0%, #000000 60%, #111111 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 25px 45px rgba(0, 0, 0, 0.45)",
                  }}
                  _active={{
                    bgGradient:
                      "linear(140deg, #050505 0%, #0a0a0a 55%, #111111 100%)",
                    transform: "translateY(0)",
                    boxShadow: "0 15px 30px rgba(0, 0, 0, 0.35)",
                  }}
                >
                  Send OTP
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleVerifyOtp}>
                <FormControl mb={5}>
                  <FormLabel color={secondaryTextColor} fontWeight="600">
                    OTP
                  </FormLabel>
                  <Input
                    name="otp"
                    type="text"
                    placeholder="Enter the OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="signin-input"
                    _focus={{
                      borderColor: focusColor,
                      boxShadow: `0 0 0 1px ${focusColor}`,
                    }}
                  />
                  {errors.otp && (
                    <Text color="red.300" fontSize="sm" mt={2}>
                      {errors.otp}
                    </Text>
                  )}
                </FormControl>

                <Button
                  type="submit"
                  width="100%"
                  className="signin-button"
                  py={6}
                  mb={4}
                  isLoading={submitting}
                  bgGradient="linear(140deg, #111111 0%, #000000 55%, #1a1a1a 100%)"
                  color="white"
                  _hover={{
                    bgGradient:
                      "linear(140deg, #050505 0%, #000000 60%, #111111 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 25px 45px rgba(0, 0, 0, 0.45)",
                  }}
                  _active={{
                    bgGradient:
                      "linear(140deg, #050505 0%, #0a0a0a 55%, #111111 100%)",
                    transform: "translateY(0)",
                    boxShadow: "0 15px 30px rgba(0, 0, 0, 0.35)",
                  }}
                >
                  Verify OTP
                </Button>

                <Text
                  textAlign="center"
                  color={secondaryTextColor}
                  fontSize="sm"
                  cursor="pointer"
                  onClick={() => setStep(1)}
                >
                  Didn't get a code? Try a different email
                </Text>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleResetPassword}>
                <FormControl mb={5}>
                  <FormLabel color={secondaryTextColor} fontWeight="600">
                    New Password
                  </FormLabel>
                  <Input
                    name="newPassword"
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="signin-input"
                    _focus={{
                      borderColor: focusColor,
                      boxShadow: `0 0 0 1px ${focusColor}`,
                    }}
                  />
                  {errors.newPassword && (
                    <Text color="red.300" fontSize="sm" mt={2}>
                      {errors.newPassword}
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={5}>
                  <FormLabel color={secondaryTextColor} fontWeight="600">
                    Confirm Password
                  </FormLabel>
                  <Input
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="signin-input"
                    _focus={{
                      borderColor: focusColor,
                      boxShadow: `0 0 0 1px ${focusColor}`,
                    }}
                  />
                  {errors.confirmPassword && (
                    <Text color="red.300" fontSize="sm" mt={2}>
                      {errors.confirmPassword}
                    </Text>
                  )}
                </FormControl>

                <Button
                  type="submit"
                  width="100%"
                  className="signin-button"
                  py={6}
                  isLoading={submitting}
                  bgGradient="linear(140deg, #111111 0%, #000000 55%, #1a1a1a 100%)"
                  color="white"
                  _hover={{
                    bgGradient:
                      "linear(140deg, #050505 0%, #000000 60%, #111111 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 25px 45px rgba(0, 0, 0, 0.45)",
                  }}
                  _active={{
                    bgGradient:
                      "linear(140deg, #050505 0%, #0a0a0a 55%, #111111 100%)",
                    transform: "translateY(0)",
                    boxShadow: "0 15px 30px rgba(0, 0, 0, 0.35)",
                  }}
                >
                  Reset Password
                </Button>
              </form>
            )}

            <Text textAlign="center" mt={6} color={secondaryTextColor}>
              Remembered your password?{" "}
              <Link
                to="/auth/signin"
                style={{ color: focusColor, fontWeight: "bold" }}
              >
                Sign In
              </Link>
            </Text>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}

export default ForgotPassword;